import React, { forwardRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactPhoneInput from 'react-phone-input-2';
import el from '../../locale/react-phone-input-2/el.json';

import { subYears, addYears, isBefore } from 'date-fns';

import {
  useAddDriver,
  useEditDriver,
  useGetDriverForm,
} from '../../hooks/react-query/useDrivers';
import { TextField } from '../ui';
import { Checkbox } from '../ui/Checkbox';
import { dateFormatter } from '../../utils/formatter';
import { FormSkeletion } from '../elements/skeletions';
import { SpinnerIcon } from '../../icons';
import { toast } from '../ui/Toast/useToast';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { CalendarIcon } from '../../icons/calendarIcon';
import moment from 'moment';
import * as locale from "date-fns/locale";
import clsx from 'clsx';
import usePersonStore from '../../store/personStore';
import useIsMobile from '../../utils/useIsMobile';

registerLocale("el", locale["el"]);

export const DriverForm = ({
  mode = 'create',
  driversDetails,
  btnText = 'Αποθήκευση',
  handleClose,
  setSelectedDriver,
}) => {
const isMobile = useIsMobile(768);
  const { data: driverFormObj, isLoading } = useGetDriverForm(); 
  const { mutate: addNewDriver, isLoading: isAdding } = useAddDriver();
  const { mutate: editDriver, isLoading: isUpdating } = useEditDriver();

  const [error, setError] = useState({});

  const assignedRoles = usePersonStore((store) => store.userRoles);
  const hasFleetAdminRole = assignedRoles.includes('fleetadmin');

  const CustomDateInput = forwardRef(({ value, onClick, className }, ref) => <button type="button" className={className} onClick={onClick} ref={ref}>
      <p>{value || "dd/mm/yyyy"}</p>
    </button>);

  if (isLoading) {
    return <FormSkeletion />;
  }

  return (
    <Formik
      initialValues={driverFormObj?.data?.reduce((result, item) => {
        if (mode === 'create') {
          if (item.name === 'active') {
            result[item.name] = false;
          } else {
            result[item.name] = '';
          }
        } else {
          result[item.name] = driversDetails[item.name];
        }
        return result;
      }, {})}
      validateOnChange={true}
      isInitialValid={false}
      validateOnBlur={isMobile}
      validationSchema={Yup.object({
        // move to seperate file
        firstName: Yup.string().required(''),
        lastName: Yup.string().required(''),
        email: Yup.string()
          .email('Το email πρέπει να είναι έγκυρο.')
          .required(''),
        birthDate: Yup.date()
          .nullable()
          .transform((curr, orig) => (orig === '' ? null : curr))
          .required(''),
        driversLicense: Yup.string()
          .matches(
            '^[α-ωΑ-Ωa-zA-Z0-9]*$',
            'Ο αριθμός διπλώματος θα πρέπει να αποτελείται μόνο από γράμματα και αριθμούς.'
          )
          .required(''),
        driversLicenseIssueDate: Yup.date().required(''),
        driversLicenseExpirationDate: Yup.date().required(''),
        phone: Yup.string().required(''),
      })}
      onSubmit={async (values, { setErrors }) => {
        const fieldData = { ...values, phone: `+${values['phone']}` };

        if (mode === 'create') {
          addNewDriver(fieldData, {
            onSuccess: (data) => {
              if (data?.data?.driverDetails) {
                handleClose && handleClose(data.data.driverDetails.user_id);
              } else {
                const { exceptions } = data;
                if (exceptions?.invalid) {
                  let errorObj = {};
                  Object.keys(exceptions.invalid).forEach((key) => {
                    errorObj[key] = exceptions.invalid[key][0];
                  });
                  setErrors(errorObj);
                }
              }
            },
            onError: () => {
              toast({
                variant: 'destructive',
                description: 'Παρουσιάστηκε κάποιο πρόβλημα',
              });
            },
          });
        } else {
          editDriver(
            { driverId: driversDetails.id, payload: values },
            {
              onSuccess: (response) => {
                if (response && response.data === 'success') {
                  setSelectedDriver && setSelectedDriver({...driversDetails, ...values})
                  handleClose && handleClose();
                } else {
                  const { exceptions } = response;
                  if (exceptions?.invalid) {
                    let errorObj = {};
                    Object.keys(exceptions.invalid).forEach((key) => {
                      errorObj[key] = exceptions.invalid[key][0];
                    });
                    setErrors(errorObj);
                  }
                }

                /* if (response.exceptions) {
                  if (size(response.exceptions) > 0) {
                    const exceptionsArray = isArray(response.exceptions);
                    const message = exceptionsArray
                      ? response.exceptions.slice(-1)
                      : 'Παρουσιάστηκε κάποιο πρόβλημα';
                    toast({
                      variant: 'destructive',
                      description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
                    });
                  }
                } */
              },
              onError: () => {
                toast({
                  variant: 'destructive',
                  description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                });
              },
            }
          );
        }
      }}
    >
      {({
        errors,
        touched,
        values,
        setFieldValue,
        isValid,
        dirty,
        handleBlur,
        setFieldTouched,
        ...props
      }) => {
        return (
          <Form
            tabIndex="0"
            className={`w-full no-scrollbar justify-between 
            max-lg:h-[calc(100vh-130px)]
            flex flex-col lg:max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar pb-2 pb-6 items-center`}
          >
            <div className="grid sm:grid-cols-2 gap-5 driver-form lg:min-w-[500px] lg:max-w-[700px]">
              {driverFormObj?.data?.map((formElement) => {
                const { type, label, name } = formElement;
                if (type === 'bool') {
                  return (
                    <div className="items-center flex space-x-2" key={name}>
                      <Checkbox
                        id={name}
                        checked={!!values[name]}
                        onCheckedChange={(value) => {
                          setFieldValue(name, value);
                        }}
                      />
                      <div className="grid gap-1.5 leading-none">
                        {/*change in backend text from frontend*/}
                        <label
                          htmlFor={name}
                          className="text-label text-fadedgrey"
                        >
                          Ενεργός (υποχρεωτικό)
                        </label>
                      </div>
                    </div>
                  );
                }
                if (formElement.type === 'date') {
                  let pickerMaxDate = null;
                  let pickerMinDate = null;

                  if (formElement.name === 'birthDate') {
                    pickerMaxDate = dateFormatter(subYears(new Date(), 18));
                    pickerMinDate = dateFormatter(subYears(new Date(), 100));
                  }

                  if (formElement.name === 'driversLicenseIssueDate') {
                    pickerMaxDate = dateFormatter(new Date());
                  }

                  if (
                    formElement.name === 'driversLicenseIssueDate' &&
                    values?.driversLicenseExpirationDate
                  ) {
                    pickerMaxDate = isBefore(
                      values?.driversLicenseExpirationDate,
                      dateFormatter(new Date()) // Date.now()
                    )
                      ? values?.driversLicenseExpirationDate
                      : pickerMaxDate;
                  }

                  if (
                    formElement.name === 'driversLicenseIssueDate' &&
                    values?.birthDate
                  ) {
                    pickerMinDate = addYears(values?.birthDate, 16);
                  }

                  if (formElement.name === 'driversLicenseExpirationDate') {
                    pickerMinDate = dateFormatter(new Date()); //Date.now();
                  }

                  if (
                    formElement.name === 'driversLicenseExpirationDate' &&
                    values?.driversLicenseIssueDate
                  ) {
                    pickerMinDate = isBefore(
                      values?.driversLicenseIssueDate,
                      dateFormatter(new Date())
                      // Date.now()
                    )
                      ? dateFormatter(new Date()) //Date.now()
                      : values?.driversLicenseIssueDate;
                  }

                  // let selectedValue = values[formElement.name];
                  /* if (type === 'edit' && !values[formElement.name]) {
                    if (defaultValue) {
                      selectedValue = parse(
                        defaultValue,
                        'yyyy-MM-dd',
                        new Date()
                      );
                    }
                  } */

                  let initValue = ''
                  if (values[name] !== '' && values[name] !== null && !!values[name]) {
                    const [year, month, day] = values[name].split('-');
                      initValue =new Date(year, month - 1, day);
                  }

                   if (isMobile) {
                    return (
                      <>
                      <div className="flex flex-col gap-1">
                        <p className="text-label text-[#C1C1C1]">{label}</p>
                        <ReactDatePicker
                          id={name}
                          key={name}
                          locale="el"
                          minDate={pickerMinDate}
                          maxDate={pickerMaxDate}
                          selected={initValue}
                          dateFormat="dd/MM/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          withPortal={isMobile} // portal only on mobile
                          customInput= {isMobile && <CustomDateInput className={clsx("focus:ring-none flex gap-2 rounded-[8px] border border-[#A5A5A5] p-2 bg-white w-full",
                            touched[name] && errors[name]
                            ? 'border border-primary'
                            : 'border border-[#A5A5A5]'
                          )} />}
                          enableTabLoop={false}
                          onCalendarClose={() => setFieldTouched(name, true)}
                          onChange={(date) =>
                            setFieldValue(name, date ? moment(date).format("YYYY-MM-DD") : "")
                          }
                          />
                        <div className="flex flex-col gap-1">
                          {!!touched[name] && errors[name] && (
                            <p className="text-label text-primary">
                              {errors[name]}
                          </p>
                          )}
                        </div>
                      </div>
                    </>
                    )
                  } else {
                    return ( <div className="flex flex-col gap-1">
                        <p className="text-label text-[#C1C1C1]">{label}</p>
                        <ReactDatePicker
                          id={name}
                          key={name}
                          locale={'el'}
                          minDate={pickerMinDate}
                          maxDate={pickerMaxDate}
                          calendarIconClassname="top-[2px]"
                          icon={<CalendarIcon className={'stroke-black'} />}
                          showIcon
                          className={clsx("focus:ring-none flex gap-2 rounded-[8px] border border-[#A5A5A5] p-2 bg-white w-full",
                          touched[name] && errors.hasOwnProperty(name)
                            ? 'border border-primary'
                            : 'border border-[#A5A5A5]'
                          )}
                          enableTabLoop={false}
                          placeholderText="dd/mm/yyyy"
                          dateFormat='dd/MM/yyyy'
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode='select'
                          selected={initValue}
                          onCalendarClose={() => { setFieldTouched(name, true) } }
                          onChange={(date) => { setFieldValue(name, date !== null ? moment(date).format('YYYY-MM-DD'):'') }}
                        />
                        {!!touched[name] && errors[name] && (
                        <p className="text-label text-primary">
                            {errors[name]}
                        </p>
                        )}
                    </div>)
                  }
                }
                if (formElement.type === 'phone') {
                  return (
                    <div className="flex flex-col gap-1" key={name}>
                      <label
                        htmlFor={name}
                        className="text-label text-fadedgrey"
                      >
                        {label}
                      </label>
                      <ReactPhoneInput
                        key={name}
                        id={name}
                        country="gr"
                        countryCodeEditable={false}
                        autoFormat={false}
                        containerClass={`flex gap-2 rounded-[8px] p-2 ${
                          touched[name] && errors.hasOwnProperty(name)
                            ? 'border border-primary'
                            : 'border border-[#A5A5A5]'
                        }`}
                        inputClass="outline-0 w-full placeholder:text-label"
                        localization={el}
                        specialLabel="Phone"
                        value={values[name]}
                        placeholder=""
                        onChange={(value) => {
                          const addedValue = mode !=='create' ? '+' +value : value 
                          setFieldValue(name, addedValue);
                        }}
                        onBlur={handleBlur}
                        inputProps={{
                          name: formElement.name,
                          required: true,
                        }}
                      />
                      {!!touched[name] && errors[name] && (
                        <p className="text-label text-primary">
                          {errors[name]}
                        </p>
                      )}
                    </div>
                  );
                }
                return (
                  <TextField
                    disabled = {(name === "email" && mode === "edit" && hasFleetAdminRole) ? true : false}
                    isDriverEmailDisabled = {hasFleetAdminRole && mode === "edit"}
                    key={name}
                    id={name}
                    autocomplete="off"
                    placeholder={label}
                    defaultValue={values[name]}
                    {...formElement}
                    onChange={(e) => {
                      error[name] && delete error[name];
                      setError(error);
                      setFieldValue(name, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={!!touched[name] && errors[name]}
                    className={
                      touched[name] && errors.hasOwnProperty(name)
                        ? 'border border-solid border-primary'
                        : ''
                    }
                  />
                );
              })}
            </div>
            <div className="flex max-lg:flex-col max-lg:w-full max-lg:flex-col-reverse gap-y-3 items-center justify-center gap-x-3.5 pt-6">
              {mode === 'edit' && (
                <button
                  type="button"
                  className="btn-secondary text-button-primary max-lg:w-9/12 items-center justify-center"
                  onClick={handleClose}
                >
                 Ακύρωση
                </button>
              )}
              <button
                type="submit"
                className="btn-primary text-button-primary flex gap-2 max-lg:w-9/12 items-center justify-center"
                disabled={!isValid || isAdding || isUpdating}
              >
                {(isAdding || isUpdating) && <SpinnerIcon />} {btnText}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default DriverForm;
